/* mm-header: behaviour for the global chrome (sections/mm-announcement.liquid, sections/mm-header.liquid).
   <mm-marquee>  seamless announcement loop: measures one set of messages, clones it until two equal halves cover the
                 bar, then slides the track by -50% forever. Static under prefers-reduced-motion.
   <mm-header>   sticky modes (always / when scrolling up), desktop mega menus (hover with intent delay, keyboard focus,
                 first tap opens on touch, Escape closes), the phone drawer (<dialog>: focus stays inside, Escape and the
                 scrim close it, page scroll locked while open; +/- opens one sub-list at a time), the locale popover
                 (button toggles it; outside click / Escape close it), search modal hand-off, and the theme editor
                 (selecting a Nav item or one of its Menu card blocks opens that panel / drawer row).
   Custom elements re-initialise themselves when the theme editor re-renders a section. Loaded by both sections. */
(() => {
  if (window.MMChrome) return;
  window.MMChrome = true;

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 760px)');

  /* ---------- Announcement marquee ---------- */
  define('mm-marquee', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.group = this.querySelector('[data-group]');
      if (!this.track || !this.group) return;
      this.queue = () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.build()); };
      this.ro = new ResizeObserver(this.queue);
      this.ro.observe(this);
      this.ro.observe(this.group);
      reduceMotion.addEventListener?.('change', this.queue);
      document.fonts?.ready?.then(this.queue);
      // Pause button beside the marquee (WCAG 2.2.2): shown only while the loop runs.
      this.toggle = this.parentElement?.querySelector('[data-ann-toggle]');
      this.toggle?.addEventListener('click', () => {
        const on = !this.hasAttribute('data-paused');
        this.toggleAttribute('data-paused', on);
        this.toggle.setAttribute('aria-pressed', String(on));
        this.toggle.setAttribute('aria-label', on ? this.toggle.dataset.play : this.toggle.dataset.pause);
      });
      this.build();
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      reduceMotion.removeEventListener?.('change', this.queue);
      cancelAnimationFrame(this.raf);
    }

    build() {
      const groupWidth = this.group.getBoundingClientRect().width;
      const boxWidth = this.clientWidth;
      if (reduceMotion.matches || !groupWidth || !boxWidth) { this.clear(); this.removeAttribute('data-running'); if (this.toggle) this.toggle.hidden = true; return; }
      const perHalf = Math.max(1, Math.ceil(boxWidth / groupWidth));
      const clones = perHalf * 2 - 1;
      if (this.clones !== clones || Math.abs((this.groupWidth || 0) - groupWidth) > .5) {
        this.clear();
        const frag = document.createDocumentFragment();
        for (let i = 0; i < clones; i += 1) frag.append(this.copy());
        this.track.append(frag);
        this.clones = clones;
        this.groupWidth = groupWidth;
        const speed = Math.max(5, Number(this.dataset.speed) || 40);
        this.style.setProperty('--mm-ann-dur', `${((groupWidth * perHalf) / speed).toFixed(2)}s`);
      }
      this.setAttribute('data-running', '');
      if (this.toggle) this.toggle.hidden = false;
    }

    clear() {
      this.track.querySelectorAll('[data-clone]').forEach((node) => node.remove());
      this.clones = 0;
      this.groupWidth = 0;
    }

    copy() {
      const node = this.group.cloneNode(true);
      node.removeAttribute('data-group');
      node.setAttribute('data-clone', '');
      node.setAttribute('aria-hidden', 'true');
      node.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      node.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      return node;
    }
  });

  /* ---------- Header ---------- */
  define('mm-header', class extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const on = (target, type, fn, opts = {}) => target?.addEventListener(type, fn, { signal: this.ac.signal, ...opts });
      this.root = this.closest('.mm-header') || this;
      this.section = this.root.parentElement;
      this.nav = this.querySelector('[data-nav]');
      this.items = [...this.querySelectorAll('[data-panel-item]')];
      this.drawer = this.querySelector('[data-drawer]');
      this.burger = this.querySelector('[data-drawer-open]');

      // Mega menus: hover intent on mouse, open on keyboard focus, first tap opens on touch screens.
      // Closed panels are inert, so nothing inside them takes focus or clicks while they fade out.
      for (const item of this.items) {
        const link = item.querySelector('[data-panel-link]');
        const panel = item.querySelector('.mm-header__panel');
        if (panel) panel.inert = !item.classList.contains('is-open');
        on(item, 'pointerenter', (e) => { if (e.pointerType !== 'touch') this.hover(item); });
        on(item, 'pointerleave', (e) => { if (e.pointerType !== 'touch') this.hover(null); });
        on(item, 'focusin', (e) => { if (!item.contains(e.relatedTarget)) this.open(item); });
        on(item, 'focusout', (e) => { if (e.relatedTarget && !item.contains(e.relatedTarget)) this.close(item); });
        on(link, 'pointerdown', (e) => { this.tapToOpen = e.pointerType === 'touch' && !item.classList.contains('is-open'); });
        on(link, 'click', (e) => {
          if (!this.tapToOpen) return;
          e.preventDefault();
          this.tapToOpen = false;
          this.open(item);
        });
      }
      on(document, 'pointerdown', (e) => {
        if (this.current && !this.current.contains(e.target)) this.close(this.current);
        if (this.locale && !this.locale.contains(e.target)) this.toggleLocale(false);
      });
      on(this, 'keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.localePop && !this.localePop.hidden) { this.toggleLocale(false); this.localeBtn?.focus(); return; }
        if (!this.current) return;
        const item = this.current;
        this.close(item);
        item.querySelector('[data-panel-link]')?.focus();
      });

      // Locale popover: the country / language buttons submit Shopify's localization form.
      this.locale = this.querySelector('[data-locale]');
      this.localeBtn = this.locale?.querySelector('[data-locale-toggle]');
      this.localePop = this.locale?.querySelector('.mm-locale__pop');
      on(this.localeBtn, 'click', () => this.toggleLocale());

      // Drawer sub-lists: one open at a time.
      this.subs = [...this.querySelectorAll('[data-sub-toggle]')];
      for (const btn of this.subs) on(btn, 'click', () => this.toggleSub(btn));

      // Drawer
      on(this.burger, 'click', () => this.openDrawer());
      on(this.drawer, 'click', (e) => {
        if (e.target === this.drawer || e.target.closest('[data-drawer-close]')) this.closeDrawer();
        else if (e.target.closest('a[href]')) this.closeDrawer(true);
      });
      on(this.drawer, 'cancel', (e) => { e.preventDefault(); this.closeDrawer(); });
      on(desktop, 'change', (e) => { if (e.matches) this.closeDrawer(true); else this.closeAll(); });

      // Search: Horizon's predictive search modal when it's on the page, else the link goes to /search.
      on(this, 'click', (e) => {
        const trigger = e.target.closest('[data-mm-search-open]');
        const modal = document.getElementById('search-modal');
        if (!trigger || typeof modal?.showDialog !== 'function') return;
        e.preventDefault();
        this.closeAll();
        this.closeDrawer(true);
        modal.showDialog();
      });

      // Theme editor: selecting a Nav item (or one of its cards) shows its panel.
      on(document, 'shopify:block:select', (e) => this.editor(e, true));
      on(document, 'shopify:block:deselect', (e) => this.editor(e, false));

      this.sticky(on);
    }

    disconnectedCallback() {
      this.ac?.abort();
      this.ro?.disconnect();
      clearTimeout(this.hoverTimer);
      clearTimeout(this.closeTimer);
      if (this.drawer?.open || this.closing) document.documentElement.classList.remove('mm-scroll-lock');
      this.section?.classList.remove('mm-header-hidden');
    }

    /* Locale popover */
    toggleLocale(force) {
      if (!this.localePop) return;
      const open = force ?? this.localePop.hidden;
      this.localePop.hidden = !open;
      this.localeBtn?.setAttribute('aria-expanded', String(open));
      if (open) {
        this.closeAll();
        const on = this.localePop.querySelector('[aria-current]');
        if (on) on.parentElement.scrollTop = on.offsetTop - on.parentElement.offsetTop;
      }
    }

    /* Drawer sub-lists */
    toggleSub(btn, force) {
      const open = force ?? btn.getAttribute('aria-expanded') !== 'true';
      for (const other of this.subs) {
        const on = other === btn ? open : false;
        other.setAttribute('aria-expanded', String(on));
        const sign = other.querySelector('[data-sign]');
        if (sign) sign.textContent = on ? '−' : '+';
        const sub = document.getElementById(other.getAttribute('aria-controls'));
        if (sub) sub.hidden = !on;
      }
    }

    /* Mega menus */
    hover(item) {
      clearTimeout(this.hoverTimer);
      if (item) {
        this.hoverTimer = setTimeout(() => this.open(item), this.current ? 0 : 90);
        return;
      }
      this.hoverTimer = setTimeout(() => {
        // Keep a panel open while keyboard focus is inside it.
        if (this.current && !this.current.querySelector(':focus-visible')) this.close(this.current);
      }, 180);
    }

    open(item) {
      if (!item) return;
      this.toggleLocale(false);
      if (this.current && this.current !== item) this.close(this.current);
      item.classList.add('is-open');
      item.querySelector('[data-panel-link]')?.setAttribute('aria-expanded', 'true');
      const panel = item.querySelector('.mm-header__panel');
      if (panel) panel.inert = false;
      this.current = item;
    }

    close(item) {
      if (!item) return;
      item.classList.remove('is-open');
      item.querySelector('[data-panel-link]')?.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('.mm-header__panel');
      if (panel) panel.inert = true;
      if (this.current === item) this.current = null;
    }

    closeAll() {
      clearTimeout(this.hoverTimer);
      this.items.forEach((item) => this.close(item));
    }

    /* Drawer */
    openDrawer() {
      const dialog = this.drawer;
      if (!dialog || dialog.open) return;
      this.closeAll();
      clearTimeout(this.closeTimer);
      this.closing = false;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('mm-scroll-lock');
      this.burger?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    }

    closeDrawer(instant = false) {
      const dialog = this.drawer;
      if (!dialog?.open) return;
      if (this.closing && !instant) return;
      this.closing = true;
      dialog.classList.remove('is-open');
      const done = () => {
        clearTimeout(this.closeTimer);
        if (!this.closing) return;
        this.closing = false;
        if (typeof dialog.close === 'function') dialog.close();
        dialog.removeAttribute('open');
        document.documentElement.classList.remove('mm-scroll-lock');
        this.burger?.setAttribute('aria-expanded', 'false');
        if (!desktop.matches) this.burger?.focus({ preventScroll: true });
      };
      if (instant || reduceMotion.matches) done();
      else this.closeTimer = setTimeout(done, 340);
    }

    /* Sticky: publishes the header height (anchor offsets) and runs the "when scrolling up" mode. */
    sticky(on) {
      const mode = this.dataset.sticky;
      if (!mode || mode === 'none') return;
      const measure = () => {
        const height = `${Math.round(this.root.getBoundingClientRect().height)}px`;
        document.documentElement.style.setProperty('--mm-header-h', height);
        document.body?.style.setProperty('--header-height', height);
      };
      this.ro = new ResizeObserver(measure);
      this.ro.observe(this.root);
      if (mode !== 'scroll-up') return;
      // Horizon's "squeeze" layout scrolls .page-wrapper instead of the window from 990px up (assets/base.css).
      const wrapper = document.querySelector('.page-wrapper');
      const scrollTop = () => (wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper.scrollTop : window.scrollY);
      let lastY = scrollTop();
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const y = Math.max(0, scrollTop());
          const delta = y - lastY;
          if (Math.abs(delta) < 6) return;
          const hide = delta > 0 && y > this.root.offsetHeight + 80 && !this.current && !this.drawer?.open;
          this.section?.classList.toggle('mm-header-hidden', hide);
          if (hide) this.closeAll();
          lastY = y;
        });
      };
      on(window, 'scroll', onScroll, { passive: true });
      on(wrapper, 'scroll', onScroll, { passive: true });
    }

    /* Theme editor */
    editor(e, selected) {
      if (e.detail?.sectionId !== this.dataset.sectionId) return;
      const key = ` ${e.detail.blockId} `;
      if (desktop.matches) {
        const item = this.items.find((el) => el.dataset.blocks?.includes(key));
        if (item) { if (selected) this.open(item); else this.close(item); }
        return;
      }
      const row = [...(this.drawer?.querySelectorAll('[data-blocks]') || [])].find((el) => el.dataset.blocks.includes(key));
      if (!row) return;
      if (selected) {
        this.openDrawer();
        const btn = row.querySelector('[data-sub-toggle]');
        if (btn) this.toggleSub(btn, true);
      } else this.closeDrawer(true);
    }
  });
})();
